import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { Prayer } from '@/types/prayer';
import { useVoiceCapture } from './useVoiceCapture';
import { prayerEditorAuth } from '@/lib/prayer-authoring';
import { prayerUpdateDraft } from '@/lib/prayer-update-draft-store';
import { revisePrayerAttempt } from '@/lib/content-lifecycle/store';
import { refreshPrayerEnrichment } from '@/lib/prayer-enrichment';
import {
  audioQueueMetaDeletionAllowed,
  requiresEnhancedVoiceProcessing,
  useAudioQueueStore,
} from '@/lib/audio-queue-store';
import {
  finalizeCommittedAudio,
  recoveredAudioClaimAllowed,
  retryDestinationAudio,
  saveRefusedForUnreadableRecording,
} from '@/lib/audio-queue';
import {
  containsRecoveredVoiceText,
} from '@/lib/audio-recovery';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import { getAuthUserId } from '@/lib/keepsake/epochs';
import {
  mintProgressEntryId,
} from '@/lib/progress-entry-identity';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import { getCurrentContentLifecycleContext, syncContentLifecycle } from '@/lib/content-lifecycle/sync';

interface UsePrayerUpdateOptions {
  prayer: Prayer;
  voiceTarget: string;
  initialNote?: string;
  onCommit?: () => void;
}

export function usePrayerUpdate({
  prayer,
  voiceTarget,
  initialNote = '',
  onCommit,
}: UsePrayerUpdateOptions) {
  const [auth] = useState(prayerEditorAuth);
  const draft = useMemo(() => prayerUpdateDraft(auth, prayer.id, initialNote), [auth, prayer.id, initialNote]);
  const state = useSyncExternalStore(draft.subscribe, draft.getSnapshot, draft.getSnapshot);
  const active = draft.isCurrent();
  const ready = active && state.loading === 'ready';
  const note = active ? state.note : '';
  const isSaving = state.operation !== null;
  const voice = useVoiceCapture();
  const registerVoiceTarget = voice.registerTarget;
  const mountedDraft = useRef<typeof draft | null>(null);
  useLayoutEffect(() => {
    mountedDraft.current = draft;
    void draft.load(true);
    return () => { mountedDraft.current = null; };
  }, [draft]);
  const setNote = useCallback((value: string) => draft.setNote(value), [draft]);
  const queueItems = useAudioQueueStore((s) => s.items);
  const recoveryItems = useMemo(
    () =>
      queueItems.filter(
        (item) =>
          item.prayerId === prayer.id &&
          item.recoveryKind === 'progress-update' &&
          item.ownerId === getAuthUserId() &&
          item.audioProtocolVersion === 1 &&
          item.localDisposition === 'unresolved' &&
          (item.destinationState === undefined || item.destinationState === 'unresolved'),
      ),
    [queueItems, prayer.id],
  );

  const isThisTarget = voice.target === voiceTarget;
  const isListening = voice.isRecording && isThisTarget;
  const enhancedVoiceEnabled = enhancedVoiceAllowed();
  // Byte-bearing recovery rows are deliberately retained while Enhanced Voice
  // is disabled, even if text is already ready. They are parked recovery
  // material, not active work: treating them as pending would leave the editor
  // stuck on WRITING, while merging them would duplicate a retained row on the
  // next mount. Only intentional text/plain zero-byte recovery can merge off.
  const hasPendingRecovery =
    enhancedVoiceEnabled &&
    recoveryItems.some(
      (item) => !item.failed && requiresEnhancedVoiceProcessing(item),
    );
  const hasFailedRecovery = recoveryItems.some(
    (item) => item.failed && requiresEnhancedVoiceProcessing(item),
  );
  const hasParkedRecovery =
    !enhancedVoiceEnabled &&
    recoveryItems.some(
      (item) => !audioQueueMetaDeletionAllowed(item, false),
    );
  const canRetryRecovery = enhancedVoiceEnabled && hasFailedRecovery;
  const isTranscribing =
    (voice.isTranscribing && isThisTarget) || hasPendingRecovery;
  const interim = isThisTarget ? voice.interim : '';

  // The record and callbacks stay stable while typing. A keystroke must never
  // unregister a live recording target or create a second authoring session.
  useEffect(() => registerVoiceTarget(voiceTarget, {
    onFinal: (text, queueId) => draft.receive(text, queueId, true),
    getRecoveryText: () => draft.getRecoveryText(),
  }), [registerVoiceTarget, voiceTarget, draft]);

  const lastInterim = useRef('');
  useLayoutEffect(() => { lastInterim.current = interim; }, [interim]);
  useLayoutEffect(() => () => {
    // Preserve displayed browser words before the registration cleanup stops
    // and stores the capture. This never cancels or retargets a recording.
    draft.receive(lastInterim.current, undefined, true);
  }, [draft]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      for (const item of recoveryItems) {
        const text = item.transcribedText?.trim();
        if (text && audioQueueMetaDeletionAllowed(item, enhancedVoiceEnabled)) draft.receive(text, item.id);
      }
    });
    return () => { cancelled = true; };
  }, [draft, enhancedVoiceEnabled, recoveryItems]);

  const displayValue = interim
    ? note
      ? note + ' ' + interim
      : interim
    : note;

  const canSubmit = ready && state.resolution === 'none' && !isSaving && !isListening && !isTranscribing && Boolean(note.trim());

  const toggleMic = useCallback(() => {
    if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().operation) return;
    if (isListening) {
      draft.receive(interim, undefined, true);
      void voice.stop().catch(() => {});
      return;
    }
    draft.recoveryText = '';
    voice.start(voiceTarget, {
      recoveryDestination: { kind: 'progress-update', prayerId: prayer.id },
    });
  }, [draft, isListening, interim, voice, voiceTarget, prayer.id]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!ready || isSaving) return;
    if (isListening || interim) void voice.stop().catch(() => {});
    draft.setNote(e.target.value);
  }, [draft, ready, isSaving, isListening, interim, voice]);

  const commitUpdate = useCallback(async () => {
    if (isListening || isTranscribing || !draft.getSnapshot().note.trim()) return false;
    const captured = draft.begin('save');
    if (!captured) return false;
    let committed = false;
    const excluded = new Set(captured.claimedIds);
    try {
      await draft.load(true);
      if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().resolution !== 'none' || draft.session !== captured.session) return false;
      const entry = { id: captured.entry?.id ?? mintProgressEntryId(),
        date: captured.entry?.date ?? new Date().toISOString(), note: captured.note.trim() };
      draft.entry = entry;
      const recoveryById = new Map(useAudioQueueStore.getState().items.map((item) => [item.id, item]));
      const recoveryClaims: RecoveredAudioCommitClaim[] = [];
      for (const queueItemId of captured.claimedIds) {
        const item = recoveryById.get(queueItemId);
        const recovered = item?.transcribedText?.trim() ?? '';
        if (item?.prayerId === prayer.id && item.recoveryKind === 'progress-update' &&
            containsRecoveredVoiceText(entry.note, recovered) && await recoveredAudioClaimAllowed(queueItemId)) {
          recoveryClaims.push({ queueItemId, recoveryKind: 'progress-update',
            progressEntryId: entry.id, expectedTranscribedText: recovered });
        }
      }
      const intent = { kind: 'append' as const, entry };
      let result;
      try { result = await captured.session.save(prayer.id, intent, recoveryClaims); }
      finally { draft.rememberSubmittedAttempt(captured.session, captured.revision, intent, recoveryClaims); }
      if (result.kind !== 'committed') { await draft.load(true); draft.patch({ saveError: true }); return false; }
      committed = true;
      for (const claim of result.consumedAudio) excluded.delete(claim.queueItemId);
      if (!draft.isCurrent()) return true;
      void syncContentLifecycle().then(async () => {
        for (const claim of result.consumedAudio) await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
      }).catch(() => {});
      void refreshPrayerEnrichment(prayer.id).catch(() => {});
      return true;
    } catch (error) {
      draft.patch({ saveError: true, saveNeedsReopen: saveRefusedForUnreadableRecording(error) });
      return false;
    } finally {
      draft.finish(captured.revision, committed, excluded);
      if (committed && draft.isCurrent()) {
        if (draft.getSnapshot().note) draft.patch({ savedWithNewWords: true });
        else if (mountedDraft.current === draft) {
          try { onCommit?.(); } catch { /* the local commit already succeeded */ }
        }
      }
    }
  }, [draft, isListening, isTranscribing, prayer.id, onCommit]);

  const discardDraft = useCallback(async (expectedRevision: number) => {
    if (draft.getSnapshot().revision !== expectedRevision) {
      draft.patch({ discardError: true });
      return false;
    }
    const captured = draft.begin('discard');
    if (!captured) return false;
    let discarded = false;
    try {
      await draft.load(true);
      if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().resolution === 'changed' || draft.session !== captured.session) {
        draft.patch({ discardError: true }); return false;
      }
      // Unknown here means the exact attempt is absent. Explicit Discard may
      // clear this page's words, but never deletes/recreates another attempt.
      if (captured.session.attempt && draft.getSnapshot().resolution !== 'unknown') {
        const context = await getCurrentContentLifecycleContext(draft.auth);
        await revisePrayerAttempt(context, captured.session.attempt, null);
      }
      if (!draft.isCurrent()) return false;
      discarded = true;
      return true;
    } catch { draft.patch({ discardError: true }); return false; }
    finally { draft.finish(captured.revision, discarded, captured.claimedIds); }
  }, [draft]);

  const retryRecovery = useCallback(async () => {
    // Call-site defense as well as a render gate: retained legacy audio must
    // not enter a cloud retry path while the independent voice gate is shut.
    if (!draft.isCurrent() || draft.getSnapshot().loading !== 'ready' || draft.getSnapshot().operation || !enhancedVoiceAllowed()) return;
    draft.patch({ saveError: false, saveNeedsReopen: false });
    try {
      for (const item of recoveryItems.filter(
        (row) => row.failed && requiresEnhancedVoiceProcessing(row),
      )) {
        if (!draft.isCurrent()) return;
        await retryDestinationAudio(item.id);
      }
    } catch {
      draft.patch({ saveError: true });
    }
  }, [draft, recoveryItems]);

  const retainedWordItems = recoveryItems.filter((item) => draft.excludedIds.has(item.id) &&
    item.transcribedText?.trim() && audioQueueMetaDeletionAllowed(item, enhancedVoiceEnabled));
  const useRecordingWords = useCallback(() => {
    for (const item of retainedWordItems) draft.useRecordingWords(item.transcribedText!, item.id);
  }, [draft, retainedWordItems]);
  const keepDraft = useCallback(() => {
    draft.receive(lastInterim.current, undefined, true);
    return !draft.getSnapshot().operation;
  }, [draft]);
  const retryLoad = useCallback(() => { void draft.load(true); }, [draft]);
  const commitSuccessor = useCallback(async () => {
    if (!draft.chooseSuccessor()) return false;
    return commitUpdate();
  }, [draft, commitUpdate]);

  return {
    note,
    revision: state.revision,
    resolution: state.resolution,
    commitSuccessor,
    canSaveSuccessor: ready && state.resolution === 'saved' && !isSaving && !isListening && !isTranscribing && Boolean(note.trim()),
    displayValue,
    isListening,
    isTranscribing,
    isSupported: voice.isSupported,
    interim,
    setNote,
    toggleMic,
    handleChange,
    commitUpdate,
    keepDraft,
    discardDraft,
    ready,
    loading: state.loading,
    retryLoad,
    discardError: state.discardError,
    savedWithNewWords: state.savedWithNewWords,
    hasRetainedRecording: recoveryItems.length > 0,
    hasRecordingWords: retainedWordItems.length > 0,
    useRecordingWords,
    canSubmit,
    isSaving,
    saveError: state.saveError,
    saveNeedsReopen: state.saveNeedsReopen,
    hasFailedRecovery,
    hasParkedRecovery,
    canRetryRecovery,
    retryRecovery,
  };
}
